import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import { QuickAmounts } from "@/components/QuickAmounts";
import { storage } from "@/lib/storage";
import { getCurrency, formatMoney } from "@/lib/currencies";
import { Nfc, Check, Smartphone } from "lucide-react";

export default function ReceiveNFC() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(storage.getCurrency());
  const [stage, setStage] = useState("entry"); // entry | waiting | done
  const [cardMasked, setCardMasked] = useState("");

  const cur = getCurrency(currency);
  const valid = Number(amount) > 0;

  const startTap = () => {
    setStage("waiting");
    // Simulate NFC tap (works offline)
    const t = setTimeout(() => {
      const last4 = Math.floor(1000 + Math.random() * 9000);
      setCardMasked(`•••• •••• •••• ${last4}`);
      storage.addTransaction({
        direction: "received",
        amount: Number(amount),
        currency,
        method: "nfc",
        card: `**** ${last4}`,
      });
      setStage("done");
      setTimeout(() => navigate("/merchant"), 1600);
    }, 2600);
    return () => clearTimeout(t);
  };

  useEffect(() => {
    if (stage !== "waiting") return;
    // allow user to cancel by changing stage via back - no auto cleanup needed
  }, [stage]);

  return (
    <div className="page-shell" data-testid="receive-nfc-page">
      <Header title="NFC / Card Tap" subtitle="Hold card near device" />

      {stage === "entry" && (
        <div className="card-glass gold-glow rounded-2xl p-6 fade-slide-up">
          <div className="flex items-center justify-between mb-4">
            <span className="stat-label">Amount to charge</span>
            <CurrencyPicker value={currency} onChange={setCurrency} compact />
          </div>
          <div className="flex items-center gap-3 mb-6">
            <span className="text-3xl font-bold text-[#E6B455]">{cur.symbol}</span>
            <input
              type="number"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="flex-1 bg-transparent text-5xl font-bold text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/20 min-w-0"
              data-testid="nfc-amount-input"
              autoFocus
            />
          </div>
          <QuickAmounts currency={currency} onPick={setAmount} selected={amount} />
          <button
            onClick={startTap}
            disabled={!valid}
            className="btn-gold w-full py-3.5 rounded-xl font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="nfc-start-btn"
          >
            <Nfc size={18} /> Start NFC Reader
          </button>
          <p className="text-center text-[11px] text-[#F7E6B4]/45 mt-4">
            Works fully offline. Card data is validated locally.
          </p>
        </div>
      )}

      {stage === "waiting" && (
        <div className="card-glass rounded-3xl p-8 flex flex-col items-center text-center fade-slide-up">
          <div className="relative mb-8">
            <div className="w-40 h-40 rounded-full border gold-border flex items-center justify-center nfc-pulse">
              <Nfc size={56} className="text-[#E6B455]" />
            </div>
            <div className="absolute inset-0 rounded-full border gold-border nfc-ring" />
            <div className="absolute inset-0 rounded-full border gold-border nfc-ring" style={{ animationDelay: "0.6s" }} />
          </div>
          <h2 className="text-xl font-bold text-[#F7E6B4] mb-2" data-testid="nfc-waiting-title">
            Hold card near device
          </h2>
          <p className="text-sm text-[#F7E6B4]/60 max-w-xs mb-5">
            <Smartphone size={14} className="inline mr-1" /> Charging {formatMoney(amount, currency)}
          </p>
          <button
            onClick={() => setStage("entry")}
            className="btn-outline-gold px-5 py-2 rounded-full text-sm"
            data-testid="nfc-cancel-btn"
          >
            Cancel
          </button>
        </div>
      )}

      {stage === "done" && (
        <div className="card-glass gold-glow rounded-3xl p-8 flex flex-col items-center text-center fade-slide-up" data-testid="nfc-done">
          <div className="w-20 h-20 rounded-full bg-[rgba(230,180,85,0.15)] flex items-center justify-center mb-5">
            <Check size={44} className="text-[#E6B455]" />
          </div>
          <h2 className="text-2xl font-bold gold-text mb-2">Payment approved</h2>
          <div className="text-3xl font-bold text-[#F7E6B4] mb-2">{formatMoney(amount, currency)}</div>
          <div className="text-sm text-[#F7E6B4]/60 font-mono">{cardMasked}</div>
        </div>
      )}
    </div>
  );
}
